import React, { useState, useRef, useEffect, useCallback } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { QRCodeSVG, QRCodeCanvas } from 'qrcode.react';
import { jsPDF } from 'jspdf';
import { getProfileModules } from '../../config/profilesConfig';
import '../../styles/Layout.css';

const API_URL = process.env.REACT_APP_API_URL || '/api';

function Sidebar({ isOpen, toggleSidebar, user }) {
  const baseMenuItems = getProfileModules(user.role, user.tipoUsuario);
  const location = useLocation();
  const [expandedMenus, setExpandedMenus] = useState({});
  const [capturaStatus, setCapturaStatus] = useState('');
  const [subiendoCaptura, setSubiendoCaptura] = useState(false);
  const capturaInputRef = useRef(null);

  // Estado para módulos habilitados (solo para Estudiante y Docente)
  const [modulosHabilitados, setModulosHabilitados] = useState(null);
  const [loadingModulos, setLoadingModulos] = useState(false);

  // Cargar módulos habilitados desde la API
  const cargarModulosHabilitados = useCallback(async () => {
    // Solo aplicar filtro para Estudiante y Docente de COLEGIO
    if (user.tipoUsuario !== 'COLEGIO' || !['Estudiante', 'Docente'].includes(user.role)) {
      setModulosHabilitados(null);
      return;
    }

    if (!user.esquema) return;

    setLoadingModulos(true);
    try {
      const token = sessionStorage.getItem('token') || localStorage.getItem('token');
      const perfil = user.role.toUpperCase();

      const response = await fetch(`${API_URL}/modulos-perfil/${user.esquema}/${perfil}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        // Crear un Set con los IDs de módulos habilitados
        const habilitados = new Set(
          data.modulos
            .filter(m => m.habilitado)
            .map(m => m.id)
        );
        setModulosHabilitados(habilitados);
      } else {
        // Si falla, mostrar todos los módulos
        setModulosHabilitados(null);
      }
    } catch (error) {
      console.error('Error cargando módulos habilitados:', error);
      // Si falla, mostrar todos los módulos
      setModulosHabilitados(null);
    } finally {
      setLoadingModulos(false);
    }
  }, [user.tipoUsuario, user.role, user.esquema]);

  useEffect(() => {
    cargarModulosHabilitados();
  }, [cargarModulosHabilitados]);

  // Filtrar módulos según configuración
  const menuItems = React.useMemo(() => {
    // Si no hay filtro o está cargando, mostrar todos
    if (modulosHabilitados === null) {
      return baseMenuItems;
    }

    // Filtrar los items que están habilitados
    return baseMenuItems.filter(item => modulosHabilitados.has(item.id));
  }, [baseMenuItems, modulosHabilitados]);

  const handleMenuClick = () => {
    // En movil, cerrar el sidebar despues de seleccionar
    if (window.innerWidth <= 768) {
      toggleSidebar();
    }
  };

  // Verificar si un submenu debe estar expandido (si la ruta actual coincide con algun subitem)
  const isSubMenuActive = (item) => {
    if (!item.subItems) return false;
    return item.subItems.some(sub => location.pathname === sub.path || location.pathname.startsWith(sub.path + '/'));
  };

  // Verificar si el menu debe mostrarse expandido
  // Prioridad: estado manual del usuario > auto-expansion por ruta activa
  const isMenuExpanded = (item) => {
    const manualState = expandedMenus[item.id];
    // Si el usuario ha establecido un estado manual, respetarlo
    if (manualState !== undefined) {
      return manualState;
    }
    // Si no hay estado manual, auto-expandir si hay subruta activa
    return isSubMenuActive(item);
  };

  const toggleSubMenu = (item) => {
    const currentlyExpanded = isMenuExpanded(item);
    setExpandedMenus(prev => ({
      ...prev,
      [item.id]: !currentlyExpanded
    }));
  };

  // Auto-expandir menu si una subruta esta activa (solo si no hay estado manual)
  useEffect(() => {
    menuItems.forEach(item => {
      if (item.subItems && isSubMenuActive(item) && expandedMenus[item.id] === undefined) {
        setExpandedMenus(prev => ({ ...prev, [item.id]: true }));
      }
    });
  }, [location.pathname, menuItems]);

  // Handler para pegar imagen desde clipboard
  const handleCapturaPaste = async (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;

    for (const item of items) {
      if (item.type.startsWith('image/')) {
        e.preventDefault();
        const blob = item.getAsFile();
        if (!blob) continue;

        setSubiendoCaptura(true);
        setCapturaStatus('Subiendo...');

        try {
          const formData = new FormData();
          formData.append('imagen', blob, 'captura.jpg');

          const token = sessionStorage.getItem('token') || localStorage.getItem('token');
          const response = await fetch(`${API_URL}/captura`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`
            },
            body: formData
          });

          const result = await response.json();
          if (result.success) {
            setCapturaStatus('✓ Guardada');
            setTimeout(() => setCapturaStatus(''), 2000);
          } else {
            setCapturaStatus('✗ Error');
            setTimeout(() => setCapturaStatus(''), 3000);
          }
        } catch (error) {
          console.error('Error subiendo captura:', error);
          setCapturaStatus('✗ Error');
          setTimeout(() => setCapturaStatus(''), 3000);
        } finally {
          setSubiendoCaptura(false);
        }
        break;
      }
    }
  };

  // Generar PDF con QR grande para imprimir
  const qrCanvasRef = useRef(null);

  const generarPDFQR = () => {
    const canvas = qrCanvasRef.current?.querySelector('canvas');
    if (!canvas) return;

    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const pageWidth = doc.internal.pageSize.getWidth();

    // Título
    doc.setFontSize(28);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0, 51, 102);
    doc.text('UAcademiK', pageWidth / 2, 35, { align: 'center' });

    // Subtítulo
    doc.setFontSize(14);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100, 100, 100);
    doc.text('Plataforma de Gestión Universitaria', pageWidth / 2, 45, { align: 'center' });

    // QR centrado
    const qrSize = 120;
    const qrX = (pageWidth - qrSize) / 2;
    const qrY = 65;
    const qrImage = canvas.toDataURL('image/png');
    doc.addImage(qrImage, 'PNG', qrX, qrY, qrSize, qrSize);

    // Instrucción
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(30, 30, 30);
    doc.text('Escanea este código QR para acceder', pageWidth / 2, qrY + qrSize + 15, { align: 'center' });
    doc.text('a la plataforma UAcademiK', pageWidth / 2, qrY + qrSize + 25, { align: 'center' });

    // Nombre del colegio y año
    doc.setFontSize(13);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(80, 80, 80);
    if (user.colegioNombre) {
      doc.text(user.colegioNombre, pageWidth / 2, qrY + qrSize + 40, { align: 'center' });
    }
    if (user.colegioAnio) {
      doc.text(`Período académico ${user.colegioAnio}`, pageWidth / 2, qrY + qrSize + 50, { align: 'center' });
    }

    // URL
    doc.setFontSize(10);
    doc.setTextColor(120, 120, 120);
    doc.text(window.location.origin, pageWidth / 2, qrY + qrSize + 62, { align: 'center' });

    doc.save(`QR_UAcademiK_${user.colegioNombre || 'universidad'}.pdf`);
  };

  return (
    <>
      <div
        className={`sidebar-overlay ${isOpen ? 'active' : ''}`}
        onClick={toggleSidebar}
      ></div>
      <div className={`sidebar ${isOpen ? 'open' : ''}`}>
        <nav className="sidebar-menu">
          {loadingModulos ? (
            <div className="sidebar-loading">
              <span className="loading-spinner-small"></span>
            </div>
          ) : (
            menuItems.map(item => {
              const hasSubItems = item.subItems && item.subItems.length > 0;
              const isExpanded = isMenuExpanded(item);

              if (hasSubItems) {
                return (
                  <div key={item.id} className="menu-item-with-submenu">
                    <button
                      className={`menu-item menu-item-expandable ${isSubMenuActive(item) ? 'active' : ''}`}
                      onClick={() => toggleSubMenu(item)}
                    >
                      <span className="menu-icon">{item.icon}</span>
                      <span className="menu-label">{item.label}</span>
                      <span className={`menu-arrow ${isExpanded ? 'expanded' : ''}`}>
                        {isExpanded ? '▼' : '▶'}
                      </span>
                    </button>
                    {isExpanded && (
                      <div className="submenu">
                        {item.subItems.map(subItem => (
                          <NavLink
                            key={subItem.id}
                            to={subItem.path}
                            className={({ isActive }) => `submenu-item ${isActive ? 'active' : ''}`}
                            onClick={handleMenuClick}
                          >
                            <span className="submenu-icon">{subItem.icon}</span>
                            <span className="submenu-label">{subItem.label}</span>
                          </NavLink>
                        ))}
                      </div>
                    )}
                  </div>
                );
              }

              return (
                <NavLink
                  key={item.id}
                  to={item.path}
                  className={({ isActive }) => `menu-item ${isActive ? 'active' : ''}`}
                  onClick={handleMenuClick}
                >
                  <span className="menu-icon">{item.icon}</span>
                  <span className="menu-label">{item.label}</span>
                </NavLink>
              );
            })
          )}
        </nav>

        {/* Área para pegar capturas de pantalla */}
        <div className="sidebar-captura">
          <input
            ref={capturaInputRef}
            type="text"
            className={`captura-input ${subiendoCaptura ? 'subiendo' : ''} ${capturaStatus.includes('✓') ? 'exito' : ''} ${capturaStatus.includes('✗') ? 'error' : ''}`}
            placeholder={capturaStatus || 'Ctrl+V captura'}
            onPaste={handleCapturaPaste}
            readOnly
            title="Haz clic aquí y pega una imagen (Ctrl+V)"
          />
        </div>

        {/* Info del colegio en la parte inferior (solo para usuarios de colegio) */}
        {user.tipoUsuario === 'COLEGIO' && (
          <div className="sidebar-footer">
            <div className="sidebar-colegio-info">
              <span className="colegio-icon">🏫</span>
              <div className="colegio-details">
                <span className="colegio-nombre">{user.colegioNombre}</span>
                {user.role === 'Estudiante' && user.cursoNombre && (
                  <span className="colegio-curso">{user.cursoNombre}</span>
                )}
                <span className="colegio-anio">Año {user.colegioAnio}</span>
              </div>
            </div>
            <div className="sidebar-qr" onClick={generarPDFQR} title="Clic para generar PDF imprimible con QR">
              <QRCodeSVG value={window.location.origin} size={80} level="M" />
              <span className="sidebar-qr-hint">Clic para imprimir</span>
            </div>
            {/* Canvas oculto para generar imagen del QR en el PDF */}
            <div ref={qrCanvasRef} style={{ position: 'absolute', left: '-9999px', top: '-9999px' }}>
              <QRCodeCanvas value={window.location.origin} size={512} level="M" />
            </div>
          </div>
        )}

        {/* Info para usuarios del sistema */}
        {user.tipoUsuario === 'SISTEMA' && (
          <div className="sidebar-footer">
            <div className="sidebar-colegio-info sistema">
              <span className="colegio-icon">⚡</span>
              <div className="colegio-details">
                <span className="colegio-nombre">UAcademik Pro</span>
                <span className="colegio-anio">Sistema Central</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

export default Sidebar;
